import React, { useState, useMemo } from "react";
import { toast } from "sonner";
import { filterAndSort, sortArr } from "@/lib/helpers";
import { exportExcel, exportPDF } from "@/lib/exports";
import PaginationBar from "@/components/PaginationBar";

const blank = { nombre: "", cif: "", telefono: "", email: "", direccion: "", notas: "" };

export default function Clientes({ state, upsert, remove }) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "nombre", dir: 1 });
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(blank);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(25);

  const toggleSort = (field) =>
    setSort((s) => (s.field === field ? { field, dir: -s.dir } : { field, dir: 1 }));

  const allRows = useMemo(
    () => filterAndSort(state.clientes, search, sort, state),
    [state.clientes, search, sort, state]
  );
  const pagedRows = allRows.slice(skip, skip + limit);

  const openModal = (row = null) => {
    setForm(row ? { ...blank, ...row } : { ...blank });
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    await upsert("clientes", form);
    toast.success("Guardado correctamente");
    setModal(false);
  };

  const doExport = (fn, kind) => {
    const ok = fn("clientes", state);
    if (!ok) toast.error("Sin datos que exportar");
  };

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap gap-2 items-center">
        <input className="input max-w-xs" placeholder="Buscar cliente..."
               value={search} onChange={(e) => setSearch(e.target.value)}
               data-testid="search-clientes"/>
        <div className="flex-1"/>
        <button className="btn btn-ghost" onClick={() => doExport(exportExcel)} data-testid="export-clientes-excel">Excel</button>
        <button className="btn btn-ghost" onClick={() => doExport(exportPDF)} data-testid="export-clientes-pdf">PDF</button>
        <button className="btn btn-primary" onClick={() => openModal()} data-testid="btn-new-cliente">+ Nuevo cliente</button>
      </div>

      <div className="card overflow-hidden">
        <table className="data">
          <thead><tr>
            {["nombre", "cif", "telefono", "email", "direccion"].map((f) => (
              <th key={f} className={`sortable ${sort.field === f ? "active" : ""}`} onClick={() => toggleSort(f)}>
                {({nombre:"Nombre",cif:"CIF/NIF",telefono:"Teléfono",email:"Email",direccion:"Dirección"})[f]}
                <span className="arr">{sortArr(sort, f)}</span>
              </th>
            ))}
            <th/>
          </tr></thead>
          <tbody>
            {pagedRows.map((c) => (
              <tr key={c.id}>
                <td className="font-semibold">{c.nombre}</td>
                <td>{c.cif}</td>
                <td>{c.telefono}</td>
                <td>{c.email}</td>
                <td>{c.direccion}</td>
                <td className="text-right whitespace-nowrap">
                  <button className="btn btn-ghost !py-1 !px-2" onClick={() => openModal(c)}>Editar</button>
                  <button className="btn btn-danger !py-1 !px-2" onClick={() => remove("clientes", c.id)}>✕</button>
                </td>
              </tr>
            ))}
            {allRows.length === 0 && (
              <tr><td colSpan="6" className="text-center text-slate-400 py-6">No hay clientes</td></tr>
            )}
          </tbody>
        </table>
        <PaginationBar
          total={allRows.length}
          skip={skip}
          limit={limit}
          onSkipChange={setSkip}
          onLimitChange={setLimit}
        />
      </div>

      {modal && (
        <div className="modal-backdrop" onKeyDown={(e) => e.key === "Escape" && setModal(false)}>
          <div className="modal p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-bold text-lg">{form.id ? "Editar" : "Nuevo"} cliente</h3>
              <button onClick={() => setModal(false)} className="text-slate-400 hover:text-slate-700 text-2xl">✕</button>
            </div>
            <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
              <div><label className="label">Nombre *</label>
                <input className="input" required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="cliente-nombre"/></div>
              <div><label className="label">CIF/NIF</label>
                <input className="input" value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })}/></div>
              <div><label className="label">Teléfono</label>
                <input className="input" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}/></div>
              <div><label className="label">Email</label>
                <input className="input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}/></div>
              <div className="md:col-span-2"><label className="label">Dirección</label>
                <input className="input" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })}/></div>
              <div className="md:col-span-2"><label className="label">Notas</label>
                <textarea className="textarea" rows="2" value={form.notas || ""} onChange={(e) => setForm({ ...form, notas: e.target.value })}/></div>
              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" className="btn btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" data-testid="cliente-save">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
